const en = {
  tasks: {
    priority: {
      critical: '🔴 Critical',
      urgent: '🟠 Urgent',
      high: '🟡 High',
      medium: '🟢 Medium',
      low: '🔵 Low',
      label: {
        critical: 'Critical',
        urgent: 'Urgent',
        high: 'High',
        medium: 'Medium',
        low: 'Low'
      }
    },
    status: {
      pending: 'Pending',
      assigned: 'Assigned',
      inProgress: 'In Progress',
      completed: 'Completed',
      cancelled: 'Cancelled',
      cancelAction: 'Cancel Task'
    },
    editModal: {
      title: 'Edit Task',
      property: 'Property',
      bookingRef: 'Booking Ref',
      taskTitle: 'Task Title',
      taskTitleRequired: 'Task Title *',
      taskTitlePlaceholder: 'e.g. Full property cleaning',
      scheduledDate: 'Scheduled Date',
      scheduledDateRequired: 'Scheduled Date *',
      scheduledTime: 'Scheduled Time',
      scheduledTimeHint: 'Optional - 24h format',
      priority: 'Priority',
      priorityRequired: 'Priority *',
      status: 'Status',
      statusRequired: 'Status *',
      assignedTo: 'Assigned To',
      unassigned: 'Unassigned',
      notes: 'Notes',
      notesPlaceholder: 'Notes or instructions for the team...',
      notesHint: '',
      guestMessage: 'Guest Message',
      guestMessagePlaceholder: 'e.g. We will visit at 2 PM',
      guestMessageHint: 'This message will be shown to the guest when they ask about request status',
      cancel: 'Cancel',
      save: 'Save Changes',
      saving: 'Saving...',
      errorSaving: 'Failed to update task. Please try again.',
      fieldTeamReport: 'Field Team Report'
    },
    guestRequestBadge: 'Guest Request',
    type: {
      cleaning: 'Cleaning',
      maintenance: 'Maintenance',
      housekeeping: 'Housekeeping',
      restock: 'Guest supplies',
      guest_service: 'Guest Service',
      early_checkin_request: 'Early check-in request',
      late_checkout_request: 'Late checkout request'
    },
    table: {
      headers: {
        task: 'Task',
        property: 'Property',
        bookingRef: 'Booking Ref',
        priority: 'Priority',
        status: 'Status',
        scheduledDate: 'Scheduled Date',
        assignedTo: 'Assigned To',
        actions: 'Actions'
      },
      tooltip: {
        critical: 'Critical: Same-day checkout & check-in',
        urgent: 'Urgent: Checkout today',
        high: 'High: Upcoming checkout',
        medium: 'Medium: Regular booking',
        low: 'Low: Future booking'
      },
      bulk: {
        tasksSelected: 'tasks selected',
        assignTo: 'Assign to',
        markComplete: 'Mark as Complete',
        cancelSelection: 'Cancel Selection'
      },
      dropdown: {
        changeStatus: 'Change Status'
      },
      overdue: 'Overdue',
      unassigned: 'Unassigned',
      ticketRef: 'Ticket #',
      editTooltip: 'Edit',
      completeTooltip: 'Mark as Complete'
    },
    listView: {
      filters: {
        today: 'Today',
        week: 'This Week',
        month: 'This Month',
        allStatuses: 'All Statuses'
      },
      createButton: '+ Create New Task',
      loading: 'Loading tasks...',
      retry: 'Retry',
      errorLoading: 'Failed to load tasks. Please try again.',
      emptyTitle: 'No tasks',
      emptyTextProperty: 'No tasks scheduled for this property with the selected filters.',
      emptyText: 'No tasks for the selected time range and status.',
      sections: {
        critical: 'Critical - Requires immediate action',
        urgent: 'Urgent - Today\'s priority',
        high: 'High priority - Next 24-48 hours',
        medium: 'Medium priority - This week',
        low: 'Low priority - Future',
        completed: 'Completed'
      }
    },
    // The guest-checkout record, shown on every task surface
    // (tasks/manual-guest-checkout-event.md Part 2, D43). Key parity with ar.js is mandatory —
    // a missing key falls back to Arabic with a console warning, which reads as a half-translated
    // screen rather than an obvious bug. This namespace matters more than most: the cleaning crew
    // is exactly who English exists for in this product.
    guestCheckout: {
      recorded: 'Guest has checked out',
      possible: 'Possible departure',
      // The guest pressed the button on the door plaque themselves — a report, not a team
      // confirmation (D19/D62). Never "Guest has checked out": nobody verified it.
      guestReported: 'Guest reported leaving',
      sourceManual: 'Team confirmed',
      sourceGuest: 'Door plaque',
      sourceDevice: 'Smart lock',
      recordedBy: 'Recorded by'
    },
    // A supervisor corrects a clean's record (tasks/cleaning-performance-report.md D7). The original
    // is never lost — "Corrected by …" names who changed it and why.
    cleanRecord: {
      title: 'Clean record',
      startLabel: 'Start',
      noStart: 'No start recorded',
      teamLabel: 'Team',
      noTeam: 'Nobody recorded',
      correctButton: 'Correct',
      startInput: 'Actual start time',
      startHint: 'Riyadh time.',
      teamInput: 'Who took part in the clean',
      reasonInput: 'Reason for the correction',
      reasonPlaceholder: 'e.g. forgot to tap Start Cleaning',
      save: 'Save correction',
      cancel: 'Cancel',
      saved: 'Correction saved',
      failed: 'Could not save the correction — please try again',
      loadFailed: 'Could not load the clean record',
      correctedBy: 'Corrected by',
      autoFilled: 'Team recorded automatically from the "Start Cleaning" tap.',
      changedStart: 'Start time',
      changedTeam: 'Team',
      invalidReason: 'Write a reason for the correction.',
      invalidChange: 'Nothing has changed to save.',
      invalidStatus: 'A clean that has not started, or was cancelled, cannot be corrected.',
      invalidStartedAt: 'Invalid start time — it must be before the finish and within a reasonable span.',
      invalidCleaners: 'The team list is not valid.'
    },
    card: {
      overdue: 'Overdue',
      ticketRef: 'Ticket #',
      bookingRef: 'Booking Ref',
      checkout: 'Checkout',
      checkin: 'Check-in',
      checkinUnset: 'Not set',
      guest: 'Guest',
      turnoverAlert: 'Turnover window',
      turnoverHours: 'hours only!',
      turnoverUrgent: 'Very tight schedule - requires immediate action',
      statusLabel: 'Status',
      assignedToLabel: 'Assigned to',
      unassigned: 'Unassigned',
      guestMessage: 'Guest message',
      internalNotes: 'Internal notes',
      completedAt: 'Completed on',
      completedBy: 'by',
      editButton: 'Edit',
      startButton: 'Start Work',
      startButtonCleaning: 'Start Cleaning',
      undoStart: 'Undo',
      // Who took part in a clean (tasks/cleaning-performance-report.md D3/D4/D6)
      joinButton: 'I joined',
      cleanersLabel: 'Team',
      joinCrewFull: 'This clean already has its full team — nobody else can be added.',
      joinNotOpen: 'You can only join a clean while it is in progress.',
      undoStartForbidden: 'Only a supervisor can undo a start — ask them to correct it.',
      startedAt: 'Started',
      took: 'took',
      minutes: 'minutes',
      // Cleaning target timer (tasks/cleaning-timer.md D8) — "over target", never "late".
      timerLeftOf: 'left of',
      timerOver: 'over target',
      completeButton: 'Mark as Complete',
      address: 'Address',
      errorStatus: 'Failed to update status. Please try again.',
      errorAssign: 'Failed to assign task. Please try again.',
      fieldTeamReport: 'Field Team Report',
      completionNotes: 'Completion Notes',
      completionNotesPlaceholder: 'Add notes about property condition or any other observations...',
      completionRating: 'Property Condition Rating',
      stars: 'stars',
      hideDetails: 'Hide',
      showDetails: 'Details',
      completeFailed: 'Could not complete the task. Please try again.',
      completeBlockedMessage: 'Answer every checklist and supplies item before completing this task',
      completeBlockedRemainingLabel: 'Remaining',
      completeBlockedChecklist: 'Checklist',
      completeBlockedSupplies: 'Supplies'
    },
    photos: {
      upload: 'Upload Photo',
      uploading: 'Uploading...',
      before: 'Before',
      after: 'After',
      issue: 'Issue',
      completion: 'Completion',
      maxReached: 'Maximum reached (10 photos)',
      deleteConfirm: 'Delete this photo?',
      uploadFailed: 'Photo upload failed',
      tooLarge: 'File too large (max 10MB)',
      invalidType: 'Unsupported file type (JPEG, PNG, WebP only)',
      photoCount: 'photos',
      selectType: 'Photo type',
      evidenceTitle: 'Attached Photos'
    },
    checklist: {
      openButton: 'Checklist',
      suppliesButton: 'Supplies',
      title: 'Cleaning Checklist',
      suppliesTitle: 'Supplies Report',
      done: 'Done',
      notDone: 'Not done',
      na: 'N/A',
      available: 'In stock',
      needsRestock: 'Needs restock',
      addComment: 'Add a note',
      reasonPrompt: 'What was wrong?',
      commentPlaceholder: 'Short note (optional)',
      saveFailed: 'Could not save. Please try again.',
      close: 'Close',
      allAnswered: 'All items answered',
      remainingHint: 'Answer every item before completing the task',
      unanswered: 'Not answered',
      resultsTitle: 'Checklist results',
      suppliesResultsTitle: 'Supplies report',
      restockTitle: 'Supplies to restock',
      restockEmpty: 'No supplies requested',
      // What a supplies item actually contains — a hint under the label, not answerable rows.
      // Keys are CleaningTemplate itemIds (immutable). Gate: config/suppliesLabels.js
      supplyContents: {
        personal_care_amenities: '(toothbrush set · slippers · towels · shampoo · soap · roll tissue · classic tissue)',
        guest_display_screen: 'If the TV is on another input, switch it back to the welcome screen, then record the reason.'
      }
    },
    reportIssue: {
      button: 'Report an issue',
      title: 'Report an issue',
      category: 'Issue type',
      categoryPlaceholder: 'Select a type',
      electrical: 'Electrical',
      plumbing: 'Plumbing',
      furniture: 'Furniture',
      appliances: 'Appliances',
      other: 'Other',
      description: 'Description',
      descriptionPlaceholder: 'Describe the issue you found...',
      addPhoto: 'Add a photo',
      changePhoto: 'Change photo',
      submit: 'Send report',
      submitting: 'Sending...',
      cancel: 'Cancel',
      success: 'Maintenance report created',
      failed: 'Could not send the report. Please try again.',
      categoryRequired: 'Select an issue type',
      descriptionRequired: 'Enter a description'
    },
    // "Would you host this guest again?" — asked when a turnover clean is closed
    // (tasks/post-checkout-review-and-replies.md L5–L7). A bare choice: no colours, no warnings (D4).
    // `reasons` keys are the backend's codes (guestAssessmentService.REASON_CODES) — keep them in step.
    guestAssessment: {
      question: 'Would you host this guest again?',
      yes: 'Yes',
      no: 'No',
      reasonPrompt: 'Why?',
      reasons: {
        damage: 'Damage or breakage',
        smoking: 'Smoking',
        extra_guests: 'More guests than allowed',
        rules_broken: 'House rules broken',
        very_dirty: 'Unit left very dirty',
        other: 'Other'
      },
      notePlaceholder: 'Short note (optional)',
      noteRequiredPlaceholder: 'Describe what happened',
      photoLabel: 'Photo of the problem',
      addPhoto: 'Add a photo of the problem',
      photoAttached: 'Problem photo attached',
      errorRequired: 'Answer the guest question before completing the task',
      errorAnswer: 'Choose yes or no',
      errorReason: 'Pick a reason',
      errorNote: 'Add a note that explains the reason',
      errorPhoto: 'Add at least one photo of the problem',
      errorPhotoLocked: 'This photo is the evidence for the "No" answer and cannot be removed',
      modalTitle: 'Complete the clean',
      cancel: 'Cancel',
      summaryLabel: 'Host again?',
      bulkSkipped: 'Not closed because each needs the guest question answered:'
    },
    maintenance: {
      pageTitle: 'My Tasks',
      tasksRemaining: 'tasks remaining',
      allDone: 'All tasks done!',
      filterAll: 'All Tasks',
      filterMyTasks: 'Assigned to Me',
      emptyText: 'No tasks scheduled for this period.',
      searchPlaceholder: 'Search by task ID or title...'
    },
    page: {
      headerTitle: 'Task Management',
      loading: 'Loading tasks...',
      loadError: 'Failed to load tasks. Please try again.',
      totalTasks: 'Total',
      taskUnit: 'tasks',
      critical: 'critical',
      overdue: 'overdue',
      newTask: 'New Task',
      exportBtn: 'Export',
      searchPlaceholder: 'Search tasks...',
      dateAll: 'All',
      dateToday: 'Today',
      dateWeek: 'This Week',
      dateMonth: 'This Month',
      statusActive: 'Active',
      statusAll: 'All Statuses',
      statusPending: 'Pending',
      statusAssigned: 'Assigned',
      statusInProgress: 'In Progress',
      statusCompleted: 'Completed',
      priorityAll: 'All Priorities',
      propertyAll: 'All Properties',
      bookingAll: 'All Bookings',
      bookingWith: 'With Booking',
      bookingWithout: 'Without Booking',
      assigneeMine: 'Assigned to Me',
      assigneeAll: 'All Tasks',
      assigneeUnassigned: 'Unassigned',
      sourceAll: 'All Sources',
      sourcePms: 'PMS Tasks',
      sourceService: 'Cleaning Tasks',
      sourceGuest: 'Guest Requests',
      typeAll: 'All Types',
      viewCards: 'Cards',
      viewList: 'List',
      groupByPriority: 'Group by Priority',
      groupByProperty: 'Group by Property',
      groupByStatus: 'Group by Status',
      groupByDate: 'Group by Date',
      emptyTitle: 'No tasks',
      emptyText: 'No tasks match the search criteria',
      emptyCreateBtn: 'Create New Task',
      supervisorTeam: 'Team Tasks',
      supervisorMine: 'My Tasks',
      emptyMineTitle: 'No tasks assigned to you',
      emptyMineText: 'You have no tasks assigned at the moment',
      propertyUnknown: 'Unspecified',
      unassigned: 'Unassigned',
      csvTask: 'Task',
      csvProperty: 'Property',
      csvPriority: 'Priority',
      csvStatus: 'Status',
      csvDate: 'Scheduled Date',
      csvAssignee: 'Assigned To',
      groupCritical: '🔴 Critical',
      groupUrgent: '🟠 Urgent',
      groupHigh: '🟡 High',
      groupMedium: '🟢 Medium',
      groupLow: '🔵 Low',
      groupPending: 'Pending',
      groupAssigned: 'Assigned',
      groupInProgress: 'In Progress',
      groupCompleted: 'Completed'
    },
    create: {
      title: 'Create New Task',
      typeLabel: 'Task type *',
      propertyLabel: 'Property *',
      propertyLoading: 'Loading properties...',
      propertySelect: 'Select Property',
      propertyError: 'Failed to load properties',
      propertyRequired: 'Please select a property',
      taskTitleLabel: 'Task Title *',
      taskTitlePlaceholder: 'e.g. Full apartment cleaning',
      maintenanceTitlePlaceholder: 'e.g. Fix the AC leak',
      taskTitleRequired: 'Please enter a task title',
      suppliesLabel: 'Supplies needed *',
      suppliesHint: 'If the unit already has an open supplies task, these are added to it — it can only be brought forward or raised in priority.',
      suppliesRequired: 'Choose at least one item',
      mergedSuccess: '✅ Added to supplies task',
      dateLabel: 'Scheduled Date *',
      dateRequired: 'Please select a scheduled date',
      dateHint: 'Cannot select a past date',
      timeLabel: 'Scheduled Time',
      priorityLabel: 'Priority',
      assignLabel: 'Assign To',
      assignEmpty: 'Unassigned',
      assignHint: 'Leave empty if not assigned yet',
      notesLabel: 'Notes',
      notesPlaceholder: 'Any additional details or instructions...',
      submitBtn: 'Create Task',
      submitting: 'Creating...',
      cancelBtn: 'Cancel',
      success: '✅ Task created successfully!',
      error: 'Failed to create task. Please try again.',
      // The part the server refused (TASK_006 + details.field) — utils/taskErrorCodes.js
      invalidType: 'That task type cannot be created here',
      invalidProperty: 'That property is invalid or no longer exists',
      invalidTitle: 'Please enter a task title',
      invalidScheduledDate: 'The scheduled date is invalid or in the past',
      invalidPriority: 'That priority does not exist',
      invalidAssignedTo: 'That assignee is invalid',
      invalidCleaningMode: 'That cleaning purpose is invalid',
      invalidRestockItems: 'Choose at least one item from the list',
      invalidNotes: 'The notes are longer than allowed'
    },
    stats: {
      critical: 'Critical',
      pending: 'Pending',
      inProgress: 'In Progress',
      completed: 'Completed'
    },
    // "Performance reports" — per-cleaner figures (tasks/cleaning-performance-report.md step 5).
    // ⚠️ "15" and "3 hours" must match CLEAN_MIN_MINUTES / CLEAN_MAX_MINUTES (be/services/supervisorWorklist.js).
    performance: {
      tab: 'Performance reports',
      intro: 'How the cleaning team did, per person — turnover cleans completed in the period.',
      openFromTile: 'Open performance reports — the detail per cleaner',
      periods: { last7: 'Last 7 days', thisMonth: 'This month', lastMonth: 'Last month', custom: 'Custom' },
      from: 'From', to: 'To', apply: 'Show',
      error: 'Could not load the performance reports.',
      empty: 'No completed cleans in this period.',
      // Checkout-times card (tasks/checkout-times-card.md) — RECORDED times, never "when guests left".
      checkoutTimes: {
        title: 'Checkout times recorded',
        intro: 'When the guest\'s departure was recorded, for the checkouts cleaned in the period.',
        windows: { before08: 'Before 08:00', '08to10': '08–10', '10to12': '10–12', '12to14': '12–14', '14to16': '14–16', from16: 'After 16:00' },
        recorded: 'Recorded', missing: 'No time recorded',
        byGuest: 'Guest (door card)', byOps: 'Ops',
        empty: 'No recorded checkouts in this period.',
        about: 'About these times',
        note: 'Times show when the checkout was recorded in Host Me. An ops entry may be when staff found the unit empty, not the exact departure. Percentages are of recorded checkouts only.'
      },
      cleansInPeriod: 'Cleans',
      strip: { within: 'Within target', noMeasured: 'No cleans with a target', average: 'Average time', of: 'of' },
      cols: {
        cleaner: 'Cleaner', cleans: 'Cleans', soloPaired: 'Alone / with partner', average: 'Average time',
        timing: 'Against target', within: 'Within target', over: 'Over target', notMeasured: 'Not measured'
      },
      noAttributed: 'No cleans attributed to anyone in this period.',
      unattributedLine: 'not attributed — nobody recorded as doing them (a shared login, or before this update).',
      show: 'Show', hide: 'Hide',
      about: 'How the figures are counted',
      with: 'with',
      corrected: 'Corrected',
      timing: { noTarget: 'No target', missingStart: 'Not started', tooShort: 'Under 15 min', tooLong: 'Over 3 hours' },
      footnote: 'A team clean counts for each person on it, with its full time. "Within target" = finished on or before its target time. "Not measured" = not started, under 15 minutes, or over 3 hours (finished late) — left out of the average and the share; a clean with no target counts in the average only.'
    },
    attention: {
      tabWorklist: 'Task list',
      tabAttention: 'Follow-up',
      intro: 'What needs following up now — closest impact on a guest first.',
      loading: 'Loading...',
      error: 'The follow-up list could not be loaded.',
      retry: 'Try again',
      emptyTitle: 'Nothing needs following up',
      emptyHint: "Today's work is either done or still in hand.",
      counts: {
        dueToday: 'Due today',
        overdue: 'Overdue',
        openRefills: 'Open guest supplies',
        needsOwner: 'Unassigned tasks'
      },
      needsOwnerGoal: 'Repairs and refills · goal: 0',
      guestArriving: 'Guest arrives',
      runningFor: 'running for',
      targetOf: 'target',
      overTarget: 'over target',
      dueAt: 'Due',
      noDeadline: 'No deadline',
      unassigned: 'Nobody holds it',
      // Unit status rows (tasks/unit-operational-status.md §9 item 3) — a unit that needs a decision.
      unitStatus: 'Unit status',
      openUnit: 'Open unit',
      reviewDueAt: 'Review due',
      unitReason: {
        UNIT_STOPPED_REVIEW_DUE: 'Stopped past its review date — resume it or extend the stop',
        NO_TURNOVER_CLEAN: 'Unknown — the guest left and no clean is on record',
        ARRIVAL_TIME_UNKNOWN: 'Unknown — the guest arrival time could not be resolved',
        DEPARTURE_TIME_UNKNOWN: 'Unknown — the guest departure time could not be resolved',
        LOOKUP_FAILED: 'Unknown — the unit status could not be checked'
      },
      fromCleaning: 'from a clean',
      addedByPerson: 'added by the team',
      lateMinutes: 'minutes late',
      lateHours: 'hours late',
      lateDays: 'days late',
      chase: 'Followed up',
      chasing: 'Recording...',
      chasedOnce: 'chased once',
      chasedTimes: 'chases',
      chaseFailed: 'The chase could not be recorded.',
      openFailed: 'The task could not be opened.',
      notActionable: 'The task is closed — there is nothing to chase.',
      showAll: 'See everything in the task list',
      moreItems: 'more not shown',
      cleanTime: {
        title: 'Average cleaning time',
        window: 'last 7 days',
        unit: 'minutes',
        sample: 'cleans counted',
        empty: 'No completed cleans with a recorded start in this period.'
      },
      state: {
        TASK_OVERDUE: 'Overdue',
        TASK_DUE_TODAY: 'Due today',
        TASK_ON_TIME: 'On time',
        TASK_NO_DEADLINE: 'No deadline'
      }
    }
  },
  arrivalReadiness: {
    heading: 'Today\u2019s check-in readiness',
    subtitle: 'Will today\u2019s guests get in, and do they have their instructions?',
    loading: 'Loading...',
    error: 'Could not load the readiness check — treat today\u2019s readiness as unconfirmed.',
    retry: 'Retry',
    empty: 'No check-ins today or tomorrow.',
    headline: {
      ready: 'All of today\u2019s check-ins are ready',
      readySub: 'The door code is confirmed and valid at arrival time, the check-in message is on track, and cleaning is on track.',
      action: 'Check-ins need attention',
      actionSub: 'These arrivals need attention before the guest arrives — door code, check-in message or cleaning.',
      unverified: 'Check incomplete',
      unverifiedSub: 'Some check-ins could not be verified — treat their readiness as unconfirmed.'
    },
    status: {
      ready: 'Ready',
      action: 'Needs attention',
      unverified: 'Could not verify',
      pending: 'Pending',
      na: 'Not applicable'
    },
    chip: { action: 'Needs attention', unverified: 'Could not verify', ready: 'Ready', noLock: 'No lock' },
    day: {
      today: 'Checking in today',
      todayHint: 'The door code must work on arrival',
      tomorrow: 'Checking in tomorrow',
      tomorrowHint: 'Their check-in message goes out today'
    },
    column: { guest: 'Guest', property: 'Property', time: 'Check-in time', lockCode: 'Door code', message: 'Check-in message', cleaning: 'Unit cleanliness', status: 'Status' },
    // The ops stop — fourth signal (tasks/unit-operational-status.md D17). Shown only when stopped.
    unitStopped: 'Unit stopped — move the guest or resume the unit before they arrive',
    unitStopUnverified: 'Could not check whether the unit is stopped',
    stopReason: { maintenance: 'Maintenance / fault', deep_cleaning: 'Deep cleaning', owner_use: 'Owner use', inspection: 'Inspection', other: 'Other' },
    // Keyed on the backend's machine reason code, never on its Arabic text (I18N_STRATEGY: the code is the contract). Mirror of be/services/cleaningReadiness/engine.js REASON — see web/src/utils/cleaningReasonCodes.js.
    cleaningReason: {
      CLEANING_DONE: 'Cleaning complete',
      CLEANING_ON_TRACK: 'Cleaning not started yet — there is time',
      CLEANING_DUE_SOON: 'Cleaning not started and arrival is close',
      CLEANING_IN_PROGRESS: 'Cleaning under way, not finished',
      CLEANING_OVERDUE: 'An earlier clean was never completed',
      CLEANING_WINDOW_NEGATIVE: 'The unit is occupied past the guest’s arrival time — no window to clean',
      CLEANING_WINDOW_TIGHT: 'Not enough time to clean between checkout and arrival',
      CLEANING_WINDOW_UNKNOWN: 'Could not determine when the previous guest leaves',
      CLEANING_NO_TASK: 'No cleaning task on record for this unit',
      CLEANING_UNKNOWN_ARRIVAL: 'Could not determine the arrival time',
      CLEANING_ASSESS_FAILED: 'Could not evaluate cleaning status'
    },
    // Labels for the timestamp under the cleaning verdict. Only two verdicts carry one.
    entry: { arrived: 'Guest entered' },
    // Ops confirming a guest the lock never saw (let in on an internal/emergency code).
    // Deliberately different words (and grey, not green) from «Guest entered»: a person confirmed it.
    confirmArrival: {
      action: 'Confirm check-in',
      confirmed: 'Team confirmed check-in',
      by: 'by',
      undo: 'Undo',
      timeLabel: 'Time the guest got in',
      timeHint: 'Riyadh time — change it if the guest got in earlier than now.',
      save: 'Save',
      cancel: 'Cancel',
      failed: 'Could not save — try again',
      notApplicable: 'Booking is cancelled — there is no guest to check in',
      timeInvalid: 'Invalid time — it cannot be in the future or outside the stay'
    },
    cleaningStamp: { completed: 'Completed', started: 'Started' },
    noLockSuffix: 'with no smart lock — this check does not apply.',
    lockStateAsOf: 'Lock state as of',
    lockStateSource: '(from system data, without contacting the lock directly).',
    messageDegraded: 'Message status could not be evaluated — door code status is unaffected.',
    cleaningDegraded: 'Cleaning status could not be evaluated — the other checks are unaffected.',
    manualSend: {
      action: 'Confirm manual send',
      done: 'Check-in message sent manually',
      by: 'by',
      undo: 'Undo',
      failed: 'Could not save — try again',
      notApplicable: 'This booking is messaged automatically — row refreshed'
    }
  },
  departureReadiness: {
    heading: 'Today’s check-out readiness',
    subtitle: 'Have today’s guests received their departure reminder?',
    loading: 'Loading...',
    error: 'Could not load the readiness check — treat today’s readiness as unconfirmed.',
    retry: 'Retry',
    empty: 'No check-outs today.',
    headline: {
      ready: 'All of today’s check-outs are on track',
      readySub: 'The departure reminder has gone out, or is on its way, for every guest leaving today.',
      action: 'Check-outs need attention',
      actionSub: 'These bookings need a manual send or a review — their guests did not get the reminder.',
      unverified: 'Check incomplete',
      unverifiedSub: 'The reminder status could not be evaluated for some bookings — treat them as unconfirmed.'
    },
    status: {
      ready: 'Ready',
      action: 'Needs attention',
      unverified: 'Could not verify',
      pending: 'Pending',
      na: 'Not applicable'
    },
    chip: { action: 'Needs attention', unverified: 'Could not verify', pending: 'Pending', ready: 'Ready', na: 'Not applicable' },
    automationOff: 'Check-out reminder automation is disabled system-wide — no reminder will be sent automatically today.',
    column: { guest: 'Guest', property: 'Property', time: 'Check-out time', reminder: 'Reminder', checkout: 'Check-out record', status: 'Status' },
    windowNote: 'The reminder goes out automatically one hour before check-out.',
    manualSend: {
      action: 'Confirm manual send',
      done: 'Check-out reminder sent manually',
      by: 'by',
      undo: 'Undo',
      failed: 'Could not save — try again',
      notApplicable: 'This booking is reminded automatically — row refreshed'
    },
    copy: {
      action: 'Copy reminder text',
      copied: 'Copied',
      failed: 'Could not copy'
    },
    // Check-out RECORD (tasks/manual-guest-checkout-event.md) — "did the guest actually leave?",
    // a different question from the reminder column above.
    //
    // ⚠️ Locked wording (D16): "Operations confirmed the guest had left" — never "actual
    // check-out". A button press is a human assertion, not an observation. Only a device source
    // earns the stronger word. ⚠️ "Left at" is when the GUEST left, not when the record was made.
    checkout: {
      action: 'Confirm check-out',
      // The quiet variant for a row the guest already reported: nothing is required of ops, so the
      // button is an option to correct rather than a task. Same distinction the booking page makes.
      opsOverride: 'Team confirmation',
      // ⚠️ This column's OWN vocabulary — never borrowed from `status.*` above (D19). The reminder
      // column says "Ready" because the system sent the message and can prove it; here a human
      // asserted something no device watched. The same word and the same green made a phone call
      // look as strong as the lock's evidence.
      state: {
        recorded: 'Recorded',
        // The guest pressed the door plaque themselves — their word, not a team confirmation (D19).
        recordedByGuest: 'Guest reported',
        notRecorded: 'Not recorded'
      },
      // Pre-record state is grey, not red: this records a fact, it is not an overdue task.
      notRecorded: 'Check-out not recorded yet',
      left: 'Left at',
      by: 'recorded by',
      save: 'Save',
      cancel: 'Cancel',
      undo: 'Undo',
      timeLabel: 'Time the guest left',
      // Says "Riyadh time" out loud: a datetime-local field carries no timezone, so anyone working
      // outside Saudi would otherwise enter their own clock without realising.
      timeHint: 'Riyadh time — adjust the hour if the guest left earlier.',
      failed: 'Could not save — try again',
      notApplicable: 'Booking is cancelled — no guest to leave',
      timeInvalid: 'Invalid check-out time — it cannot be in the future or before the guest arrived'
    }
  },
  // ── Push notifications (settings panel) ──────────────────────────────────────────────────────
  //
  // ⚠️ Moved here from `web/src/config/notificationLabels.js` on 2026-08-04
  // (tasks/manual-guest-checkout-event.md D31/D32). That module was Arabic-only on an explicit
  // assumption: "this surface is core-staff, so no English-locale user reaches it". The
  // `guest_checkout` type breaks it — the cleaning crew reaches the panel now, and the crew is
  // precisely who English exists for in this product.
  //
  // KEEP AT EXACT KEY PARITY WITH ar.js. A missing key falls back to Arabic with a console warning,
  // which on this screen means a cleaner sees an Arabic instruction in the middle of an English one.
  notifications: {
    loading: 'Loading notification settings...',
    browserPermission: 'Browser permission',
    enable: 'Enable notifications',
    reEnable: 'Re-enable',
    enabling: 'Enabling...',
    testSend: 'Send me a test notification',
    testing: 'Sending...',
    enabledNotice: '✅ Notifications are on for this device',
    removedNotice: 'Device removed',
    remove: 'Remove',

    typesTitle: 'Notification types',
    typesHint: 'These choices apply to all your devices, and to you only.',
    devicesTitle: 'My devices',
    devicesHint: 'Each browser on each device registers once. Remove any device you no longer use.',
    devicesEmpty: 'No device has notifications enabled yet',
    thisDevice: 'This device',
    lastAccepted: 'Last confirmed',
    neverReceived: 'No notification received yet',
    // A device is flagged on consecutive FAILURES, never on age — a phone nobody has pushed to in a
    // quiet week is not broken.
    failing: 'Recent delivery failures',

    health: {
      ok: 'Working',
      stale: 'Needs re-enabling',
      unknown: 'Unconfirmed'
    },

    permission: {
      granted: 'Allowed',
      denied: 'Blocked',
      default: 'Not requested yet',
      unsupported: 'Not supported in this browser'
    },

    status: {
      unreadableTitle: 'Could not read notification status',
      unreadableDetail: 'We could not reach the server. Refresh the page, and tell support if it keeps happening.',
      disabledTitle: 'Notifications are off',
      disabledDetail: 'Notifications are not enabled in this environment. Nothing for you to do.',
      unconfiguredTitle: 'Notifications are not set up on the server',
      unconfiguredDetail: 'Contact technical support — notifications cannot be enabled until the server is configured.',
      iosInstallTitle: 'Add Host Me to your Home Screen first',
      iosInstallDetail: 'On iPhone and iPad, notifications only arrive once the site is added to the Home Screen. Steps below.',
      unsupportedTitle: 'This browser does not support notifications',
      unsupportedDetail: 'Try Chrome, Edge or Safari on another device, or use a computer.',
      deniedTitle: 'Notifications are blocked in this browser',
      deniedDetail: 'Allow notifications for this site in your browser settings (the lock icon beside the address), then reload the page.',
      onTitle: 'Notifications are on for this device',
      onDetail: 'You will be alerted for the types enabled below. Send a test now to be sure.',
      staleTitle: 'This device needs re-enabling',
      staleDetail: 'This device was registered with old settings and will not receive notifications. Tap "Re-enable".',
      unknownTitle: 'This device\'s status is unconfirmed',
      unknownDetail: 'We cannot confirm notifications will reach this device. Tap "Re-enable" to be sure.',
      offTitle: 'Notifications are off on this device',
      offDetail: 'Turn them on to be alerted immediately without opening the system.'
    },

    iosTitle: '📲 Adding to the Home Screen',
    iosSteps: [
      'Open this site in Safari',
      'Tap the Share button at the bottom of the screen',
      'Choose "Add to Home Screen"',
      'Open Host Me from the new icon, then enable notifications from this page'
    ],

    // ⚠️ These keys are the backend registry's own keys (be/services/notifications/types.js).
    // They are persisted and contractual — renaming one here produces an unlabelled toggle, not a
    // build failure.
    types: {
      guest_message: {
        label: 'Guest messages',
        description: 'Alert when a new message arrives from a guest'
      },
      lock_incident: {
        label: 'Urgent lock incidents',
        description: 'Alert for an urgent lock problem affecting guest entry (failed entry attempts, broken code)'
      },
      // ⚠️ A separate key from lock_incident on purpose: muting battery chatter must not also mute
      // "a guest is at a door that will not open". A maintenance chore and an emergency cannot
      // share one switch.
      lock_battery: {
        label: 'Lock battery',
        description: 'Alert when a lock battery reaches a critical level and needs replacing now'
      },
      // 🔴 It informs, it never instructs (D23): "Guest has checked out", never "Start cleaning".
      guest_checkout: {
        label: 'Guest checkout',
        description: 'Alert when a guest is recorded as having left a unit you have a cleaning task for'
      },
      // One nudge only, 10 minutes before the target — nothing once it is passed (tasks/cleaning-timer.md D11).
      clean_target: {
        label: 'Cleaning target time',
        description: 'One alert 10 minutes before the target time of a clean you started'
      },
      // One person only, set in the server's config (tasks/cleaning-manager-arrival-alert.md).
      cleaning_arrival: {
        label: 'Cleaning not ready before arrival',
        description: 'Alert when a guest arrives soon and the clean has not started or finished — sent only to the person set up to receive it'
      },
      // Two bookings on the same unit for the same nights — sent once per new conflict (tasks/booking-conflict-push.md).
      booking_conflict: {
        label: 'Booking conflicts',
        description: 'Alert when two bookings overlap on the same unit for the same nights'
      },
      // A platform change the PMS refused — sent once per booking (tasks/booking-desync-alarm.md).
      booking_desync: {
        label: 'Platform changes not applied',
        description: 'Alert when a booking, change or cancellation from Airbnb or Booking.com cannot be applied by the system'
      },
      owner_new_booking: {
        label: 'New bookings',
        description: 'Alert when a new booking for your unit arrives from a booking platform'
      },
      owner_booking_cancelled: {
        label: 'Cancelled bookings',
        description: 'Alert when a booking for your unit is cancelled on a booking platform'
      },
      unknown: {
        label: 'Notification',
        description: 'A notification type this version does not recognise'
      }
    },

    // English has one plural form, so `two` deliberately repeats `many`'s shape rather than
    // inventing a dual. The Arabic side is where this distinction earns its keep.
    testSent: {
      one: '✅ Test notification sent to your device — check its screen now',
      two: '✅ Test notification sent to both your devices — check their screens now',
      many: '✅ Test notification sent to {count} of your devices — check their screens now'
    },

    errors: {
      API_004: 'Several test notifications sent. Wait a minute, then try again',
      PUSH_DISABLED: 'Notifications are not enabled in this environment',
      PUSH_NOT_CONFIGURED: 'Notifications are not set up on the server — contact technical support',
      PUSH_INVALID_SUBSCRIPTION: 'Could not register this browser. Try reloading the page, then try again',
      PUSH_DEVICE_NOT_FOUND: 'Device not found — it may have been removed from another device',
      PUSH_UNKNOWN_TYPE: 'Unknown notification type — refresh the page, your version may be out of date',
      PUSH_NO_DEVICES: 'No device has notifications enabled yet. Enable them on this device first',
      PUSH_SEND_FAILED: 'Could not send the notification — the problem is with the notification service, not you. Try again shortly',
      generic: 'Something went wrong. Please try again'
    },

    subscribeFailure: {
      unsupported: 'This browser does not support notifications',
      not_configured: 'Notifications are not set up on the server — contact technical support',
      denied: 'Notifications are blocked for this site in your browser settings. Allow them in site settings, then try again',
      dismissed: 'Permission was not granted. Tap "Enable notifications", then choose Allow',
      subscribe_failed: 'Could not enable notifications on this device. Reload the page and try again'
    }
  },
  locks: {
    tabLabel: 'Smart Locks',
    // Checkout plaque link — tasks/checkout-plaque-panel.md.
    plaque: {
      heading: 'Checkout plaque link',
      copy: 'Copy link',
      copied: 'Copied',
      // QR export — tasks/checkout-plaque-artwork-generator.md. The filename is the unit code, which
      // is what pairs the file to its apartment at the designer's end; there is no separate list.
      download: 'Download QR',
      downloadNote: 'A vector file named for the unit, ready for the designer to place into the plaque artwork. To verify after printing: scan the code — the page names the apartment.',
      downloadNoCode: 'The QR cannot be downloaded because this unit has no code — the filename is what pairs it to its apartment.',
      downloadFailed: 'Could not prepare the QR file. Try again.',
      loading: 'Loading…',
      note: 'This link is permanent and belongs to this unit alone. Once the plaque is mounted, changing it means a site visit — confirm the unit before printing or encoding.',
      noToken: 'No link has been generated for this unit yet — units added before this feature need the backfill script run first.',
      failed: 'Could not load the checkout plaque link.'
    },
    panel: {
      heading: 'Lock Status',
      noLockMapped: 'No lock mapped to this property',
      mapButton: 'Map a Lock to This Property',
      unmapButton: 'Unmap Lock',
      unmapping: 'Unmapping...',
      unmapConfirm: {
        message: 'Are you sure you want to unmap this lock? Sync will stop; the event log is preserved for audit.',
        confirm: 'Confirm Unmap',
        cancel: 'Cancel'
      },
      unmapError: 'Failed to unmap lock',
      loading: 'Loading...',
      battery: {
        label: 'Battery',
        lastReported: 'Last reported',
        unknown: 'Unknown'
      },
      // Remote-manageability, not general health — see the note in ar.js.
      online: 'Remote commands available',
      offline: 'Remote commands unavailable',
      lastSync: {
        passcodes: 'Last passcode sync',
        events: 'Last event sync',
        never: 'Never'
      },
      codes: {
        heading: 'Stored codes',
        emptyState: 'No stored codes',
        matchedGuest: 'Matched guest',
        staffOrUnknown: 'Staff / unknown',
        nonConforming: 'Non-conforming naming',
        permanent: 'Permanent',
        expired: 'Expired',
        syncing: 'Syncing',
        notYetActive: 'Not yet active',
        validUntil: 'Valid until',
        validFrom: 'Valid from'
      },
      suggestedCode: {
        heading: 'Suggested current guest code',
        displayOnlyBadge: 'Display only — the platform has not sent this to the guest',
        none: 'No suggested code',
        ambiguous: 'Ambiguous — multiple candidates',
        tooltip: 'Picked because the code name matches a currently-active booking. Never written anywhere — display only.'
      },
      events: {
        heading: 'Recent records (last 20)',
        emptyState: 'No events',
        recordType: {
          passcode: 'Passcode',
          fingerprint: 'Fingerprint',
          bluetooth: 'Bluetooth / app',
          icCard: 'IC card',
          remoteApp: 'Remote unlock',
          unknown: 'Unknown type'
        },
        success: 'Success',
        failed: 'Failed'
      },
      error: {
        title: 'Failed to load lock data',
        retry: 'Retry'
      }
    },
    mappingModal: {
      title: 'Map a Smart Lock',
      instruction: 'Select a lock to map to this property:',
      searchPlaceholder: 'Search by name, ID, or MAC...',
      noSearchMatches: 'No locks match your search',
      loading: 'Loading unmapped locks...',
      empty: 'No unmapped locks available',
      lockId: 'ID',
      battery: 'Battery',
      online: 'Remote commands available',
      offline: 'Remote commands unavailable',
      cancel: 'Cancel',
      submit: 'Map',
      submitting: 'Mapping...',
      successTitle: 'Lock mapped successfully',
      error: {
        title: 'Failed to complete mapping',
        loadFailed: 'Failed to load lock list',
        selectRequired: 'Please select a lock first'
      }
    },
    table: {
      heading: 'Smart Locks',
      subtitle: 'Fleet-wide lock status overview',
      noLocks: 'No locks mapped',
      attentionFilter: 'Attention only',
      attentionBadge: 'Needs attention',
      column: {
        lock: 'Lock',
        property: 'Property',
        battery: 'Battery',
        online: 'Status',
        lastSync: 'Last sync',
        alerts: 'Alerts'
      },
      reasons: {
        low_battery: 'Low battery',
        no_active_code: 'No active code',
        unreachable: 'Remote commands unavailable'
      },
      error: {
        title: 'Failed to load locks',
        retry: 'Retry'
      }
    }
  },

  // ── Pricing guidance (ops-only) ────────────────────────────────────────────
  // This surface GUIDES; it never suggests a price and never writes one. Nothing
  // in here may imply the system recommends a figure — that claim has not been
  // earned and a suggestion ops learns to distrust is worse than none.
  pricing: {
    page: {
      title: 'Pricing Guidance',
      subtitle: 'What this unit actually achieves, and what to quote for a stay',
      opsBadge: 'Internal tool — operations team only',
      readOnly: 'Read-only — published prices are changed in the property calendar',
      selectUnit: 'Select unit',
      noUnits: 'No units available',
      loading: 'Loading...',
      loadError: 'Could not load pricing data',
      retry: 'Retry'
    },
    common: {
      currency: 'SAR',
      perNight: 'night',
      nights: 'nights',
      of: 'of',
      unknown: 'Unknown',
      none: '—',
      listSeparator: ', ',
      // The footing beside every figure. Small and secondary — but present, because its
      // absence is exactly how someone "fixes" a number by multiplying it by 1.15 later.
      exVat: 'before VAT',
      incVat: 'incl. VAT'
    },
    // Backend failures are identified by `code`, never by the Arabic string beside it —
    // see utils/pricingErrorCodes.js.
    errors: {
      unitNotFound: 'Unit not found',
      invalidUnit: 'Invalid unit reference',
      invalidDates: 'Invalid dates — check check-in, check-out, and that the stay is under a year',
      invalidPolicy: 'Invalid settings — check the reference rate, the floor, the calendar default price and the long-stay discounts',
      invalidDiscount: 'Invalid discount — it must be between 0 and 100',
      forbidden: 'You do not have access to this data',
      system: 'System error — please try again'
    },
    scoreboard: {
      achievedRate: 'Average achieved rate from bookings',
      achievedRateWindow: 'Last 90 days',
      basedOn: 'Based on',
      occupancy: 'Forward occupancy',
      occupancyWindow: 'Next 30 days',
      occupancySold: 'sold',
      occupancySellable: 'sellable nights',
      typicalPrice: 'Rate published in the calendar',
      typicalPriceWindow: 'Median of the next 30 days',
      unsold: 'Unsold nights',
      unsoldWindow: 'Within the next 30 days',
      nothingSellable: 'Not sellable',
      nothingSellableHint: 'The unit is fully held for operations — this is not weak demand',
      heldNights: 'nights held for operations',
      noData: 'No data',
      caveatPrefix: 'The achieved rate is calculated on',
      caveatMiddle: 'nights out of',
      caveatSuffix: 'occupied — one stay has not been keyed in yet, so every rate here rests on a smaller sample than the occupancy figure',
      byChannel: 'By platform',
      byChannelHint: 'Achieved rate per platform — diagnostic only. It never feeds the floor or any quote.'
    },
    weekday: {
      heading: 'Performance by weekday',
      hint: 'Last 90 days. "Occupancy" means how often this night is filled — not how much standalone demand it has, because a stay running from the weekend also counts toward the weekdays it covers.',
      day: 'Day',
      achievedRate: 'Achieved rate',
      occupancy: 'Occupancy',
      effectiveRevenue: 'Effective revenue',
      effectiveRevenueHint: 'Revenue earned per available night — the lowest figure is the weakest day. Built from priced nights only, so it is not necessarily the product of the two columns beside it.',
      sample: 'Sample',
      quietest: 'Weakest day',
      noSample: 'No sample',
      noRateSample: 'No amounts keyed in',
      empty: 'No weekday data for this period'
    },
    days: {
      heading: 'Published prices — 30 days',
      hint: 'Click any day to open the property calendar on that date',
      date: 'Date',
      published: 'Published price',
      state: 'State',
      sold: 'Sold',
      stopSell: 'Stopped for sale',
      held: 'Held for operations',
      available: 'Available',
      noRate: 'No price',
      noLedgerRow: 'The calendar does not reach this date',
      empty: 'No days to show'
    },
    quote: {
      heading: 'Quote a stay',
      // Was "the total is the sum of the published rates" — untrue the moment a unit has a
      // commercial reference, which is the basis now. Says what the block IS instead.
      hint: 'A calculator only — nothing is saved and no booking is created',
      checkIn: 'Check-in',
      checkOut: 'Check-out',
      // The result outlives edits to the form until the next submit. Without this the
      // table reads as current — ops typed a percent, never pressed the button, and read
      // a figure back off a previous calculation with nothing on screen saying so.
      stale: 'The dates changed — press "Calculate"',
      submit: 'Calculate',
      submitting: 'Calculating...',
      nights: 'Nights',
      // The stay strip above the table. `basisTotal` is what the discount comes OFF —
      // the unit's reference × nights, or the summed published nights when it has none.
      basisTotal: 'Total before discount',
      tier: 'Long-stay discount',
      tierFrom: 'from',
      vat: 'VAT',
      incomplete: 'We cannot price this stay',
      // These three are completed by a "{n} nights" prefix in the component — they must
      // NOT repeat the noun, or the line reads "1 nights nights are stopped for sale".
      incompleteHint: 'have no published price — publish them in the property calendar first',
      forDates: 'for',
      soldWarning: 'within this range are already sold — the stay cannot be taken as quoted',
      heldWarning: 'within this range are held for a relocated guest — the unit is physically occupied and cannot be sold',
      stopSoldWarning: 'within this range are stopped for sale',
      belowFloor: 'below the floor set for this unit',
      publishedNow: 'Currently published in the calendar',
      advisory: 'Advisory only — it does not block the quote',
      error: 'Could not calculate the quote',
      invalidDates: 'Check-out must be after check-in'
    },
    economics: {
      direct: 'Direct booking',
      airbnb: 'Airbnb',
      booking: 'Booking.com',
      guestPays: 'Guest pays',
      roomRevenue: 'Revenue',
      otaFee: 'Platform fee',
      commissionBase: 'Fee base',
      hostMeCommission: 'Host Me commission',
      ownerNet: 'Owner net'
    },
    // The direct-price what-if. An INPUT, never a suggestion — how much of the platform
    // saving to pass to the guest is a commercial call per booking.
    direct: {
      channelLabel: 'Offer on',
      total: 'Guest total',
      perNight: 'Per night',
      // ONE lever, pre-filled with the unit's own tier and always measured off the base.
      // Two boxes measuring from two different starting points is what let a 15% tier and
      // a 10% offer compound to 23.5% with no figure on screen saying so.
      discountPercent: 'Discount %',
      // Names where the percent in the box came from, so a deviation is visible as one.
      unitDiscount: 'Unit discount',
      vatOn: 'Charge VAT',
      vatCaution: 'Dropping VAT is a per-unit accounting decision — a stay passing 30 nights does not by itself exempt it',
      vatWhatIf: 'per the tick above',
      noVat: 'no VAT',
      flatRateNote: 'The price you enter applies as one flat rate to every night — it replaces the calendar\'s weekend/midweek variation.',
      guestPaysNow: 'The guest pays',
      directVia: 'booking direct through Host Me',
      insteadOf: 'instead of',
      viaPlatform: 'through the other platforms',
      onChannel: 'on',
      guestSaves: 'saving'
    },
    policy: {
      heading: 'Unit settings',
      hint: 'Advisory only — they warn on a quote, they never block it. All values are before VAT, like the published calendar rate.',
      floor: 'Nightly floor',
      referenceRate: 'Reference rate',
      referenceRateHint: "The unit's normal commercial position — the quote is calculated from it",
      floorHint: 'Advisory only — warns when an offer goes below it, never blocks',
      calendarHeading: 'New calendar nights',
      calendarHint: "The unit's calendar is extended automatically every night to stay bookable 500 days ahead. Only new nights get this price — no existing night changes.",
      calendarRate: 'Calendar default price',
      calendarRateHint: 'Price published on a new night — required; without it the calendar is not extended',
      calendarWeekendUplift: 'Weekend uplift %',
      calendarWeekendUpliftHint: 'Added on new Friday and Saturday nights',
      calendarRateMissing: 'No calendar default price — this unit\'s calendar will not be extended',
      tiers: 'Long-stay discounts',
      tiersHint: 'Discounts start from 2 nights up — a 3 or 4 night discount is what fills midweek',
      minNights: 'From (nights)',
      discountPercent: 'Discount %',
      addTier: 'Add discount',
      removeTier: 'Remove',
      noTiers: 'No discounts configured',
      save: 'Save',
      saving: 'Saving...',
      saved: 'Saved',
      saveError: 'Could not save settings',
      tierIncomplete: 'Complete both the nights and the discount on every row, or remove the row',
      invalidNumber: 'Not a valid number'
    },
    // Current Airbnb discounts & promotions, read from Channex.
    // tasks/pricing-listing-build-brainstorm.md (Phase 1, slice 1a). Figures and "%" are added in code.
    offers: {
      heading: 'Current discounts & promotions on Airbnb',
      airbnbOnly: 'Booking.com and Gathern not included',
      account: 'Account',
      lastRead: 'Last read',
      unpublished: 'Listing not published on Airbnb',
      refresh: 'Refresh',
      refreshing: 'Refreshing...',
      loading: 'Loading Airbnb discounts...',
      loadError: 'Could not load Airbnb discounts',
      accountRule: 'on every price on this Airbnb account, set in Channex',
      accountRuleUnknownValue: 'Increase on this account\'s prices — value not read',
      noAccountRule: 'No increase on this account\'s prices in Channex',
      otherAccountRule: 'Another price setting on this account in Channex:',
      unknownRuleFormat: 'Unknown format:',
      promotionsLabel: 'Running promotions',
      noPromotions: 'No promotion running',
      promotion: {
        NEW_LISTING_PROMOTION: 'New listing promotion',
        HIGH_RATED_GUEST_DISCOUNT: 'High-rated guest discount',
        MOBILE_ONLY_DISCOUNT: 'Mobile-only discount'
      },
      discountsLabel: 'Discounts',
      discountColumn: 'Discount',
      valueColumn: 'Value',
      noDiscounts: 'No discounts on this listing',
      weekly: 'Weekly discount',
      monthly: 'Monthly discount',
      stayAtLeast: 'Length-of-stay discount',
      lastMinute: 'Last-minute discount',
      earlyBird: 'Early-bird discount',
      bookedBefore: 'Time from booking to arrival:',
      orMore: 'or more',
      orLess: 'or less',
      nightForms: { one: 'night', two: 'nights', few: 'nights', many: 'nights' },
      dayForms: { one: 'day', two: 'days', few: 'days', many: 'days' },
      overrideTitle: 'The running promotion cancels this unit\'s discounts',
      overrideBody: 'Airbnb applies the promotion alone — the discounts below do not reach guests while it runs.',
      changed: 'Changed',
      changeNoticed: 'Change noticed on',
      changeAfter: 'after the read of',
      changePublished: 'Listing publish state changed',
      changeRemoved: 'Removed',
      staleTitle: 'The last successful read is over two days old',
      staleBody: 'What is shown may no longer match Airbnb. Press Refresh.',
      noListing: 'No Airbnb listing number is recorded for this unit, so its discounts cannot be shown.',
      notReadYet: 'This unit has not been read from Channex yet. Press Refresh.',
      notOnAccount: 'This unit\'s listing is no longer on this Airbnb account in Channex. Check the listing link.',
      refreshCooldown: 'This account was read less than a minute ago. Try again shortly.',
      refreshFailed: 'Could not read from Channex. Showing the last saved copy.',
      refreshTimeout: 'The Channex read is slow and will be saved when it finishes. Press Refresh in a minute.',
      refreshUnavailable: 'Reading from Channex is not available in this environment.'
    }
  },
  // Guest guide — company-wide content tab + the unit guidebook additions.
  // tasks/guest-guide-sections-and-print-sheet.md
  guestGuide: {
    general: {
      tabTitle: 'General guest guide',
      intro: 'Content shared by every unit: shown on the guest guide and on the printed sheet inside the unit. Write it once here; a unit can replace the house rules with its own from its guidebook tab.',
      notSavedYet: 'Not saved yet — the emergency numbers shown are suggestions and are stored on the first save.',
      lastSaved: 'Last saved',
      arabic: 'Arabic',
      english: 'English',
      houseRules: 'House rules',
      houseRulesHint: 'One rule per line — up to 15 rules.',
      quietHours: 'Quiet hours',
      lostItems: 'Lost items',
      wasteNote: 'General waste note',
      wasteNoteHint: "Shown under each unit's own bin location — e.g. the cleaning team takes the rubbish out after check-out.",
      sustainability: 'Sustainability',
      customerServicePhone: 'Customer service number',
      socialHandle: 'Social media handle',
      socialHandleHint: 'The same username on Instagram, TikTok and Snapchat (without @). Shown with the logos at the foot of the guide and printed sheet.',
      emergencyNumbers: 'Emergency numbers',
      labelAr: 'Label (Arabic)',
      labelEn: 'Label (English)',
      number: 'Number',
      addNumber: 'Add number',
      remove: 'Remove',
      save: 'Save',
      saving: 'Saving...',
      saved: 'Saved',
      loadError: 'Could not load the general guest guide',
      saveError: 'Could not save the general guest guide',
      incompleteNumber: 'Complete the Arabic label and the number on every emergency row, or remove the row.',
      readOnly: 'Read only — editing is for ops managers and admins.'
    },
    unit: {
      guestLink: 'Guest guide link',
      guestLinkHint: 'Sent automatically with the check-in message; the same link is the QR code on the printed sheet.',
      noLinkYet: 'The link appears after the guidebook is first saved — edit any field below.',
      copy: 'Copy',
      copied: 'Link copied',
      copyFailed: 'Could not copy automatically — copy the link by hand',
      preview: 'Preview',
      print: 'Print',
      newLink: 'New link',
      newLinkTitle: 'Create a new guest guide link',
      newLinkBody: 'The current link stops working immediately — including for guests who already have it, and the QR code printed in the unit.',
      newLinkConfirm: 'I understand the printed QR code in the unit will stop working, and I will reprint the sheet.',
      newLinkUse: 'Use only if the link was posted somewhere public.',
      cancel: 'Cancel',
      createNewLink: 'Create new link',
      newLinkFailed: 'Could not create a new link',
      waste: 'Waste',
      wasteAr: 'Where the bins are (Arabic)',
      wasteEn: 'Waste / bins (English - optional)',
      unitRules: "This unit's own house rules",
      unitRulesHint: 'Leave empty to use the company rules. Anything written here replaces the company rules entirely on the guest guide and printed sheet — one rule per line, up to 15.',
      unitRulesAr: 'Rules (Arabic)',
      unitRulesEn: 'Rules (English - optional)',
      companyRules: 'Current company rules',
      companyRulesNone: 'Company rules not written yet — Messaging automation → General guest guide.',
      usingCompanyRules: 'This unit uses the company rules.',
      usingUnitRules: 'This unit uses its own rules.',
      acAr: 'AC instructions (Arabic)',
      heaterAr: 'Heater instructions (Arabic)',
      guidebookLink: 'Guidebook',
      rulesCount: 'Rules',
      rulesOverLimit: 'More than 15 rules — only the first 15 will be saved.',
      ruleTooLong: 'A rule is longer than 300 characters and will be cut.',
      acEn: 'AC instructions (English - optional)',
      heaterEn: 'Heater instructions (English - optional)',
      kitchenEn: 'Kitchen (English - optional)',
      washerEn: 'Washer / dryer (English - optional)',
      tvEn: 'TV (English - optional)'
    }
  },
  states: {
    loading: 'Loading...',
    retry: 'Retry'
  },
  ownerDashboard: {
    statsLoadError: 'Could not load your units\' figures'
  },
  unitDetails: {
    bookingsLoadError: 'Could not load this unit\'s bookings',
    calendarLoadError: 'Could not load the calendar — do not read the days shown as available until it loads'
  },
  financePage: {
    noUnits: 'There are no units in your account yet'
  },
  lists: {
    loadMoreFailed: 'Could not load more — try again',
    filtersToggle: 'Filter',
    loadMore: 'Load more',
    loadingMore: 'Loading...',
    showingOf: 'Showing {shown} of {total}'
  },
  bookingsList: {
    nightOne: '1 night',
    nightTwo: '2 nights',
    nightFew: 'nights',
    nightMany: 'nights',
    noBookings: 'No bookings yet',
    noMatches: 'No bookings match the selected filters',
    notSet: 'Not set'
  },
  guestsList: {
    noGuests: 'No guests yet',
    noMatches: 'No guests match the search or filters',
    unnamed: 'Guest',
    bookings: 'bookings',
    lastBooking: 'Last booking'
  },
  opsPerformance: {
    title: 'Operations performance',
    subtitle: 'Monthly view of guest rating and occupancy.',
    loadFailed: 'The monthly figures could not be loaded. Try again.',
    info: 'Information',
    kpi: {
      rating: 'Guest rating',
      occupancy: 'Occupancy',
      soFar: '{month} to yesterday',
      startsTomorrow: 'Counting starts tomorrow',
      reviews: '{count} {noun}',
      reviewNoun: { one: 'review', two: 'reviews', few: 'reviews' },
      nightNoun: { one: 'night', two: 'nights', few: 'nights' },
      noReviews: 'No reviews',
      nights: '{booked} of {sellable} {noun}',
      closed: '{count} closed {noun}',
      unrecorded: '{count} {noun} off sale with no reason',
      unknown: '{count} {noun} with no calendar data',
      ratingNote: 'All time: {value} from {count} {noun}. Airbnb and Booking.com only.',
      occupancyNote: 'Closed nights are left out.'
    },
    support: {
      title: 'Supporting figures',
      loading: 'Loading',
      failed: 'Could not load',
      lowSample: '(small sample)',
      replyLabel: 'Replied within an hour',
      replyInfo: 'Share of guest messages answered within an hour, over the last 7 days. A message counts once it is answered or has waited over an hour.',
      pending: 'Waiting: {count}',
      lowLabel: 'Ratings below 4.8',
      lowInfo: 'Reviews below 4.8 stars received in the last 7 days.',
      lastWeek: 'Last 7 days',
      noCheckouts: 'No checkouts in the period',
      shareLabel: 'Review share',
      shareInfo: 'Of guests who left between {from} and {to} (Airbnb / Booking only), how many left a review. Measured 14 days after checkout because reviews arrive late.',
      shareNote: '{reviewed} of {eligible} checkouts'
    }
  }
};

export default en;
